import { Component, OnInit, OnDestroy } from '@angular/core';
import { SafeUrl, DomSanitizer } from '@angular/platform-browser';

import { Subscription } from 'rxjs';

import { environment } from 'src/environments/environment';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { LoggingMessageService } from 'src/app/services/logging-message.service';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { UserNavigationService } from 'src/app/services/user-navigation.service';
import { AuthService } from 'src/app/services/auth.service';
import { WindowEventsService } from 'src/app/services/window-events.service';
import { PendoService } from 'src/app/services/pendo.service';
import { IframeService } from 'src/app/services/iframe.service';
import { PermIdIndexerEnum } from 'src/app/models/permid-indexer-enum';
import { NavigationUtils } from 'src/app/navigation-utils';
import { SubscriptionChangeService } from 'src/app/services/subscription-change.service';

@Component({
  standalone: true,
  selector: 'app-base-website',
  templateUrl: './base-website.component.html',
  styleUrls: ['./base-website.component.scss']
})

export class BaseWebsiteComponent implements OnInit, OnDestroy {
  $subscriptions: Subscription = new Subscription();
  // $navigationSubscription: Subscription;
  visibleIframe: HTMLIFrameElement;
  linkid: string;
  timer;
  currentUrl: string;
  previousUrl: string;
  visibleSrc: SafeUrl;
  fetchingToken = false;
  backButtonUrl: string;
  user = '';
  public selectedSubscriptionID: number;
  public availableSubscriptions: string;
  private showBackToDashboard = false;
  get showBackButton(): boolean {
    return this.showBackToDashboard;
  }
  set showBackButton(value: boolean) {
    this.showBackToDashboard = value;
  }

  get isDevelopment(): boolean {
    return (environment.production === false);
  }

  constructor(public router: Router,
              public route: ActivatedRoute,
              public logger: LoggingMessageService,
              public sessionService: SessionStorageService,
              public navigationService: UserNavigationService,
              public authService: AuthService,
              public windowEvents: WindowEventsService,
              public iframeService: IframeService,
              public pendoService: PendoService,
              public sanitizer: DomSanitizer,
              public subscriptionService: SubscriptionChangeService) {

    const navigationSubscription = this.router.events
      .subscribe((e: any) => {
        // If it is a NavigationEnd event display the report
        if (e instanceof NavigationEnd) {
          this.getIframeReferences();
          this.handleNavigationEvent(e);
        }
      });
    this.$subscriptions.add(navigationSubscription);
  }



  ngOnInit() {
    this.user = this.authService.getUser();

    this.subscribeToChildSiteEvents();

    this.selectedSubscriptionID = this.subscriptionService.SelectedSubscription;
    this.availableSubscriptions = this.subscriptionService.AvailableSubscriptionsString;
  }

  // Overriden by child
  subscribeToChildSiteEvents() {

  }

  subscribePortalLogin() {
    const loginSubscription = this.windowEvents.portalLogin$
      .subscribe(login => {
        if (login === true) {
          this.authService.getNewAccessToken(environment.TokenServiceApi, environment.TokenServiceMedia, environment.UseTestToken, environment.UseCookies, environment.CookieName);
        }
      });
    this.$subscriptions.add(loginSubscription);
  }

  subscribePortalLogout() {
    const indicatorSubscription = this.windowEvents.portalLogout$
      .subscribe(logout => {
        const user = this.authService.getUser();
        const isLoggedIn = this.authService.isUserLoggedIn();
        if (isLoggedIn) {
          this.logger.warn('base-website', 'subscribePortalLogout', this.selectedSubscriptionID, this.availableSubscriptions , 'Portal logout', '');
          this.authService.publishLoginStatusUpdate(false);
        }
      });
    this.$subscriptions.add(indicatorSubscription);
  }

  subscribePortalActionEvents() {
    const portalSubscription = this.windowEvents.portalAction$
      .subscribe(action => {
        this.logger.info('base-website', 'subscribePortalActionEvents', this.selectedSubscriptionID, this.availableSubscriptions ,'Portal action event received. Checking token', '');
        this.checkAccessToken();
      });
    this.$subscriptions.add(portalSubscription);
  }

  subscribeSubDomainActionEvents() {
    const subDomainSubscription = this.windowEvents.subDomainAction$
      .subscribe(action => {
        const message = `Subdomain action event received: ${action}. Checking token`;
        this.logger.info('base-website', 'subscribeSubDomainActionEvents', this.selectedSubscriptionID, this.availableSubscriptions , message, '');
        this.checkAccessToken();
      });
    this.$subscriptions.add(subDomainSubscription);
  }

  // Overriden by child
  getIframeReferences() {

  }

  private handleNavigationEvent(e: NavigationEnd) {
    this.updateLinkId();

    this.checkAccessToken(e.url);

    this.loadIframe(e.url);
    const user = this.authService.getUser();
    this.logger.debug('base-website', 'handleNavigationEvent', this.selectedSubscriptionID, this.availableSubscriptions, `Changing NavLink id: ${this.linkid}`, '');
  }

  updateLinkId() {
    const linkId = this.route.snapshot.queryParams.linkid;
    if (linkId) {
      this.linkid = linkId;
    }
  }

  checkAccessToken(url?: string) {
    if ((url) && (url.includes('login'))) {
      this.logger.info('base-website', 'checkAccessToken',  this.selectedSubscriptionID, this.availableSubscriptions ,'loading login page', '');
    } else {
      const isLoggedIn = this.authService.isUserLoggedIn();
      if (isLoggedIn) {
        this.logger.info('base-website', 'checkAccessToken', this.selectedSubscriptionID, this.availableSubscriptions , 'register token request', '');
        this.authService.registerTokenRequest(true);
      } else {
        this.logger.warn('base-website', 'checkAccessToken',  this.selectedSubscriptionID, this.availableSubscriptions ,`User Login Status: ${isLoggedIn}`, '');
        this.authService.publishLoginStatusUpdate(false);
      }
    }
  }

  setSessionVariables(path: string) {
    if (path) {
      const urlParts = path.split('/');
      if ((urlParts.length > 2) && (urlParts[urlParts.length - 2] === 'indicator')) {
        const last = urlParts[urlParts.length - 1];
        const lastParts = last.split('?');
        this.sessionService.setIndicatorId(lastParts[0]);
      } else {
        this.sessionService.setIndicatorId('0');
        this.setSessionHospital(this.linkid);
        this.setSessionUnit(this.linkid);
      }
    }
  }

  private setSessionHospital(permId: string) {
    const id = NavigationUtils.GetHospitalIdFromPermId(permId);
    this.sessionService.setUserHospitalId(id);
  }

  private setSessionUnit(permId: string) {
    const id = NavigationUtils.GetUnitIdFromPermId(permId);
    if (id === '') {
      this.sessionService.setUnitId('0');
    } else {
      this.sessionService.setUnitId(id);
    }
  }

  // Overriden by child components
  loadIframe(path: string) {

  }

  updatePendo() {
    const w: any = window;
    this.pendoService.updatePendo(w);
  }

  setHiddenIframeToIndicatorFactoryLogout() {
    this.router.navigate(['loggedout']);
  }

  // Overriden by child components
  setIframeSource(path: string) {

  }

  setIframeHeight(event: any) {
    this.reqestSizeFromIFrame();
  }

  reqestSizeFromIFrame() {
    const iframe = this.visibleIframe;
    if (iframe) {
      iframe.contentWindow.postMessage('sizing?', '*');
    }
  }

  setIframeSourceWithLogin(path: string) {
    if (path.includes(environment.LogoutPage)) {
      this.visibleSrc = this.sanitizer.bypassSecurityTrustResourceUrl(path);
    } else {
      const loginUrl = (environment.production === true) ? environment.LoginPage : `/ndnqiwrapper/assets/static/test.html`;
      this.visibleSrc = this.sanitizer.bypassSecurityTrustResourceUrl(loginUrl);
    }
    this.iframeService.setPortalFrameVisibility(true);
  }

  // Overriden by child components
  updateIframeHeight(data: any) {
  }

  // Overriden by child components
  goBack($event) {

  }

  Unsubscribe() {
    if (this.$subscriptions) {
      this.$subscriptions.unsubscribe();
    }
    // if (this.$navigationSubscription) {
    //   this.$navigationSubscription.unsubscribe();
    // }
  }

  ngOnDestroy(): void {
    this.Unsubscribe();
    clearInterval(this.timer);
  }

}
